import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  DragIndicator,
  Edit,
  Delete,
  Add,
  ArrowBack,
  Schedule,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface DealFeaturedProduct {
  id: string;
  product_id: string;
  display_order: number;
  tag: string | null;
  is_active: boolean;
  deal_end_time: string | null;
  product_name: string;
  product_slug: string;
  price: number;
  original_price: number | null;
  discount: number | null;
  brand_name: string | null;
}

interface SortableItemProps {
  product: DealFeaturedProduct;
  onToggleActive: (id: string, currentActive: boolean) => void;
  onDelete: (id: string) => void;
  onEditTag: (id: string, currentTag: string | null) => void;
  onEditDealEndTime: (id: string, currentTime: string | null) => void;
}

function SortableItem({ product, onToggleActive, onDelete, onEditTag, onEditDealEndTime }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        mb: 1,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
      secondaryAction={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={product.is_active ? '활성' : '비활성'}
            color={product.is_active ? 'success' : 'default'}
            size="small"
            onClick={() => onToggleActive(product.id, product.is_active)}
            sx={{ cursor: 'pointer' }}
          />
          <IconButton
            edge="end"
            onClick={() => onEditTag(product.id, product.tag)}
            size="small"
            title="태그 수정"
          >
            <Edit />
          </IconButton>
          <IconButton
            edge="end"
            onClick={() => onEditDealEndTime(product.id, product.deal_end_time)}
            size="small"
            color="info"
            title="종료시간 수정"
          >
            <Schedule />
          </IconButton>
          <IconButton edge="end" onClick={() => onDelete(product.id)} size="small" color="error">
            <Delete />
          </IconButton>
        </Box>
      }
    >
      <IconButton {...attributes} {...listeners} sx={{ cursor: 'move', mr: 1 }}>
        <DragIndicator />
      </IconButton>
      <Box
        sx={{
          width: 48,
          height: 48,
          bgcolor: 'grey.200',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.secondary">
          {product.display_order}
        </Typography>
      </Box>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1">{product.product_name}</Typography>
            {product.tag && (
              <Chip label={product.tag} size="small" color="primary" />
            )}
            {product.discount && (
              <Chip label={`${product.discount}% OFF`} size="small" color="error" />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2" color="text.secondary">
              {product.brand_name || '브랜드 없음'}
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="text.primary">
              {product.price.toLocaleString()}원
              {product.original_price && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1, textDecoration: 'line-through', color: 'text.secondary' }}
                >
                  {product.original_price.toLocaleString()}원
                </Typography>
              )}
            </Typography>
            {product.deal_end_time && (
              <Typography variant="caption" color="error">
                종료: {new Date(product.deal_end_time).toLocaleString('ko-KR')}
              </Typography>
            )}
          </Box>
        }
      />
    </ListItem>
  );
}

export default function DealFeaturedProducts() {
  const navigate = useNavigate();
  const [dealProducts, setDealProducts] = useState<DealFeaturedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 태그 수정 다이얼로그
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [newTag, setNewTag] = useState<string>('');

  // 종료시간 수정 다이얼로그
  const [dealEndTimeDialogOpen, setDealEndTimeDialogOpen] = useState(false);
  const [editingDealProductId, setEditingDealProductId] = useState<string | null>(null);
  const [newDealEndTime, setNewDealEndTime] = useState<string>('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch deal featured products on mount
  useEffect(() => {
    fetchDealProducts();
  }, []);

  const fetchDealProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = 'http://localhost:3005/api/v1';
      const response = await fetch(`${apiUrl}/admin/deals`);
      const result = await response.json();

      if (result.success) {
        setDealProducts(result.data);
      } else {
        throw new Error(result.message || '데이터를 불러올 수 없습니다.');
      }
    } catch (err) {
      console.error('Error fetching deal products:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = dealProducts.findIndex((p) => p.id === active.id);
    const newIndex = dealProducts.findIndex((p) => p.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newProducts = arrayMove(dealProducts, oldIndex, newIndex);
    const updatedProducts = newProducts.map((p, index) => ({
      ...p,
      display_order: index + 1,
    }));

    setDealProducts(updatedProducts);

    try {
      const apiUrl = 'http://localhost:3005/api/v1';
      const response = await fetch(`${apiUrl}/admin/deals/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: updatedProducts.map((p) => ({
            id: p.id,
            display_order: p.display_order,
          })),
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || '순서 변경 실패');
      }

      await fetchDealProducts();
    } catch (err) {
      console.error('Error reordering products:', err);
      alert(
        '순서 변경 중 오류가 발생했습니다: ' +
          (err instanceof Error ? err.message : String(err))
      );
      await fetchDealProducts();
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const apiUrl = 'http://localhost:3005/api/v1';
      const response = await fetch(`${apiUrl}/admin/deals/${id}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (result.success) {
        alert('삭제되었습니다.');
        fetchDealProducts();
      } else {
        throw new Error(result.message || '삭제 실패');
      }
    } catch (err) {
      alert(
        '삭제 중 오류가 발생했습니다: ' +
          (err instanceof Error ? err.message : String(err))
      );
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      const apiUrl = 'http://localhost:3005/api/v1';
      const response = await fetch(`${apiUrl}/admin/deals/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_active: !currentActive,
        }),
      });

      const result = await response.json();

      if (result.success) {
        fetchDealProducts();
      } else {
        throw new Error(result.message || '수정 실패');
      }
    } catch (err) {
      alert(
        '수정 중 오류가 발생했습니다: ' +
          (err instanceof Error ? err.message : String(err))
      );
    }
  };

  const handleEditTag = (id: string, currentTag: string | null) => {
    setEditingProductId(id);
    setNewTag(currentTag || '');
    setTagDialogOpen(true);
  };

  const handleSaveTag = async () => {
    if (!editingProductId) return;

    try {
      const apiUrl = 'http://localhost:3005/api/v1';
      const response = await fetch(`${apiUrl}/admin/deals/${editingProductId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tag: newTag.trim() || null,
        }),
      });

      const result = await response.json();

      if (result.success) {
        alert('태그가 수정되었습니다.');
        setTagDialogOpen(false);
        setEditingProductId(null);
        setNewTag('');
        fetchDealProducts();
      } else {
        throw new Error(result.message || '태그 수정 실패');
      }
    } catch (err) {
      alert(
        '태그 수정 중 오류가 발생했습니다: ' +
          (err instanceof Error ? err.message : String(err))
      );
    }
  };

  const handleEditDealEndTime = (id: string, currentTime: string | null) => {
    setEditingDealProductId(id);
    // ISO 8601 형식을 datetime-local 형식으로 변환 (YYYY-MM-DDTHH:mm)
    if (currentTime) {
      const date = new Date(currentTime);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      setNewDealEndTime(`${year}-${month}-${day}T${hours}:${minutes}`);
    } else {
      // 기본값: 현재 시간 + 7일
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 7);
      const year = defaultDate.getFullYear();
      const month = String(defaultDate.getMonth() + 1).padStart(2, '0');
      const day = String(defaultDate.getDate()).padStart(2, '0');
      const hours = String(defaultDate.getHours()).padStart(2, '0');
      const minutes = String(defaultDate.getMinutes()).padStart(2, '0');
      setNewDealEndTime(`${year}-${month}-${day}T${hours}:${minutes}`);
    }
    setDealEndTimeDialogOpen(true);
  };

  const handleSaveDealEndTime = async () => {
    if (!editingDealProductId) return;

    try {
      // datetime-local 형식을 ISO 8601 형식으로 변환
      const isoTime = newDealEndTime ? new Date(newDealEndTime).toISOString() : null;

      const apiUrl = 'http://localhost:3005/api/v1';
      const response = await fetch(`${apiUrl}/admin/deals/${editingDealProductId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deal_end_time: isoTime,
        }),
      });

      const result = await response.json();

      if (result.success) {
        alert('종료시간이 수정되었습니다.');
        setDealEndTimeDialogOpen(false);
        setEditingDealProductId(null);
        setNewDealEndTime('');
        fetchDealProducts();
      } else {
        throw new Error(result.message || '종료시간 수정 실패');
      }
    } catch (err) {
      alert(
        '종료시간 수정 중 오류가 발생했습니다: ' +
          (err instanceof Error ? err.message : String(err))
      );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          캣츄딜 관리
        </Typography>
      </Box>

      {/* Deal Products List */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              딜 상품 목록 ({dealProducts.length}/30)
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => alert('추가 기능은 다음 단계에서 구현됩니다.')}
              disabled={dealProducts.length >= 30}
            >
              추가
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                로딩 중...
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : dealProducts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                등록된 딜 상품이 없습니다.
              </Typography>
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={dealProducts.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <List sx={{ p: 0 }}>
                  {dealProducts.map((product) => (
                    <SortableItem
                      key={product.id}
                      product={product}
                      onToggleActive={handleToggleActive}
                      onDelete={handleDelete}
                      onEditTag={handleEditTag}
                      onEditDealEndTime={handleEditDealEndTime}
                    />
                  ))}
                </List>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Paper sx={{ mt: 3, p: 2, bgcolor: 'info.lighter' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          안내사항
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          <li>
            <Typography variant="body2">
              최대 30개의 딜 상품을 등록할 수 있습니다.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              순서는 1-30 사이의 숫자로 표시됩니다.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              왼쪽의 드래그 핸들을 클릭하여 순서를 변경할 수 있습니다.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              캣츄딜 페이지에서는 3열 그리드로 최대 30개가 표시됩니다.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              태그 예시: 베스트, 특가, 타임딜, 한정수량 등
            </Typography>
          </li>
        </Box>
      </Paper>

      {/* Tag Edit Dialog */}
      <Dialog open={tagDialogOpen} onClose={() => setTagDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>딜 태그 수정</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="태그"
            fullWidth
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="예: 베스트, 특가, 타임딜"
            helperText="비워두면 태그가 제거됩니다"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDialogOpen(false)}>취소</Button>
          <Button onClick={handleSaveTag} variant="contained">
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deal End Time Edit Dialog */}
      <Dialog open={dealEndTimeDialogOpen} onClose={() => setDealEndTimeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>딜 종료시간 수정</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="종료시간"
            type="datetime-local"
            fullWidth
            value={newDealEndTime}
            onChange={(e) => setNewDealEndTime(e.target.value)}
            helperText="딜 종료 일시를 선택하세요"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDealEndTimeDialogOpen(false)}>취소</Button>
          <Button onClick={handleSaveDealEndTime} variant="contained">
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
